import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Bot, Play, Square, Settings2, TrendingUp, Loader2 } from "lucide-react";

export default function Robots() {
  const { bots, loading, refreshAll, user } = useOutletContext();
  const [busy, setBusy] = useState(null);

  const toggle = async (bot) => {
    setBusy(bot.id);
    try {
      const { data } = await api.post(`/bots/${bot.id}/toggle`);
      toast.success(data.status === "RUNNING" ? "Bot started" : "Bot stopped");
      await refreshAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;

  return (
    <div className="px-5 pt-6 pb-6 fade-up">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Robot List</h1>
      <p className="text-[#888] text-sm mt-1 mb-6">Connected trading bots and their live status.</p>

      {bots.length === 0 ? (
        <div className="border border-[#222] bg-[#0F0F0F] p-10 text-center text-[#888]">No robots connected.</div>
      ) : (
        <div className="border-t border-[#222]">
          {bots.map((bot) => {
            const running = bot.status === "RUNNING";
            return (
              <div key={bot.id} data-testid={`robot-row-${bot.id}`}
                className="group grid grid-cols-1 md:grid-cols-[1.5fr_1fr_1fr_auto] gap-4 items-center px-5 py-5 border-b border-[#222] bg-[#0A0A0A] hover:bg-[#141414] transition-colors">
                <div className="flex items-center gap-4">
                  <div className="h-11 w-11 bg-[#111] border border-[#222] flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold">{bot.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`h-1.5 w-1.5 rounded-full ${running ? "bg-[#00F593] live-dot" : bot.status === "STOPPED" ? "bg-[#FF3B30]" : "bg-[#666]"}`} />
                      <span className={`font-mono-term text-xs uppercase tracking-widest ${running ? "text-[#00F593]" : "text-[#888]"}`}>{bot.status}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="font-mono-term text-xs uppercase tracking-widest text-[#666]">Strategy</div>
                  <div className="text-sm mt-0.5">{bot.strategy}</div>
                </div>
                <div>
                  <div className="font-mono-term text-xs uppercase tracking-widest text-[#666]">Performance</div>
                  <div className="text-sm mt-0.5 flex items-center gap-1 text-[#00F593]"><TrendingUp className="h-3.5 w-3.5" /> {bot.win_rate}% · {bot.total_trades} trades</div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="icon" data-testid={`robot-settings-${bot.id}`}
                    className="h-10 w-10 rounded-sm border-[#333] bg-transparent hover:bg-[#1A1A1A]">
                    <Settings2 className="h-4 w-4" />
                  </Button>
                  <Button onClick={() => toggle(bot)} data-testid={`robot-toggle-${bot.id}`} disabled={busy === bot.id}
                    className={`h-10 w-28 rounded-sm font-semibold transition-colors ${running ? "bg-[#FF3B30] text-white hover:bg-[#e0342a]" : "bg-white text-black hover:bg-[#e5e5e5]"}`}>
                    {busy === bot.id ? <Loader2 className="h-4 w-4 animate-spin" /> : running ? (<><Square className="mr-2 h-3.5 w-3.5" /> Stop</>) : (<><Play className="mr-2 h-3.5 w-3.5" /> Start</>)}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
